import * as LucideIcons from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { ArrowRight, Cloud, Flame, Star } from 'lucide-react';
import type { AppDefinition, AppStatus } from '../types';

const STATUS_BADGE_CLASSES: Record<
  AppStatus,
  { className: string; label: string }
> = {
  prod: {
    className: 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300',
    label: 'Production',
  },
  test: {
    className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/30 dark:text-yellow-300',
    label: 'Test actif',
  },
  dev: {
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300',
    label: 'En dev',
  },
  poc: {
    className: 'bg-violet-100 text-violet-700 dark:bg-violet-950/30 dark:text-violet-300',
    label: 'PoC finalisé',
  },
  integration: {
    className: 'bg-pink-100 text-pink-700 dark:bg-pink-950/30 dark:text-pink-300',
    label: 'Intégration',
  },
};

function DynamicIcon({
  name,
  className,
  style,
}: {
  name: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const Cmp = (LucideIcons as Record<string, LucideIcon>)[name];
  if (!Cmp) {
    const Fallback = LucideIcons.Box;
    return <Fallback className={className} style={style} />;
  }
  return <Cmp className={className} style={style} />;
}

export interface AppCardProps {
  app: AppDefinition;
  showStar: boolean;
  isFav: boolean;
  onToggleFav: () => void;
}

export function AppCard({ app, showStar, isFav, onToggleFav }: AppCardProps) {
  const st = STATUS_BADGE_CLASSES[app.status];
  const hasUrl = Boolean(app.url?.trim());
  const showFavorite = showStar && hasUrl && !app.adminOnly;
  const showAdminBadge = Boolean(app.adminOnly && hasUrl);

  const openApp = () => {
    if (hasUrl) {
      window.open(app.url, '_blank', 'noopener,noreferrer');
    }
  };

  const interactive = hasUrl;
  const cardClass = interactive
    ? 'cursor-pointer hover:shadow-md'
    : 'opacity-50 pointer-events-none';

  return (
    <article
      role={interactive ? 'link' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? openApp : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openApp();
              }
            }
          : undefined
      }
      className={`relative flex flex-col rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition-shadow dark:border-slate-700 dark:bg-slate-800 ${cardClass}`}
    >
      <div className="flex items-start gap-3">
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: app.iconBg }}
        >
          <DynamicIcon name={app.icon} className="h-4 w-4" style={{ color: app.iconColor }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{app.name}</h3>
            {showFavorite ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleFav();
                }}
                className="shrink-0 rounded p-0.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                title={isFav ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                aria-pressed={isFav}
              >
                <Star
                  className={`h-5 w-5 ${
                    isFav
                      ? 'text-amber-500 fill-amber-500 dark:text-amber-400 dark:fill-amber-400'
                      : 'text-slate-300 fill-transparent dark:text-slate-500'
                  }`}
                  strokeWidth={2}
                />
              </button>
            ) : showAdminBadge ? (
              <span className="shrink-0 rounded px-2 py-0.5 text-[10px] font-semibold bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900">
                Admin
              </span>
            ) : (
              <span
                className={`shrink-0 rounded px-2 py-0.5 text-[10px] font-medium ${st.className}`}
              >
                {st.label}
              </span>
            )}
          </div>
          <p className="mt-1 line-clamp-2 text-xs leading-snug text-slate-600 dark:text-slate-400">
            {app.desc}
          </p>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-2 dark:border-slate-700">
        <span className="flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400">
          {app.platform === 'azure' ? (
            <>
              <Cloud className="h-3.5 w-3.5" />
              Azure
            </>
          ) : (
            <>
              <Flame className="h-3.5 w-3.5 text-orange-500" />
              Firebase
            </>
          )}
        </span>
        {hasUrl ? (
          <ArrowRight className="h-4 w-4 text-slate-400" aria-hidden />
        ) : (
          <span className="text-[10px] text-slate-400">Bientôt</span>
        )}
      </div>
    </article>
  );
}
